import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  VERIFICATION_POLICY_MODES,
  type VerificationPolicyField,
  type VerificationPolicyMode,
} from '@clm/shared-types';

import { selfCertifyFieldVerificationPolicy, setFieldVerificationPolicy } from '../../api/verification-policy';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { autoApprovedQueueHref } from '../verification-queue-params';
import { PolicyProblemNotice } from './PolicyProblemNotice';
import {
  HIGH_STAKES_COPY,
  MODE_HELP,
  MODE_LABELS,
  OPT_IN_NOTE_COPY,
  OPT_IN_TAKES_EFFECT_COPY,
  STATE_COPY,
  decisionsSpoken,
  grainCopy,
  optInCopy,
  policyProblemOf,
  rateCopy,
  type PolicyProblem,
} from './shared';

/**
 * The per-field table of the Auto-approval page — CLM-108 (FR-31, R-108-3,
 * R-108-6, R-108-8, R-108-17, R-108-24). For `admin:configure` holders who are
 * Scope-All: the figures are the org-wide basis (D-107-1), which a
 * practice-scoped caller never receives (`fields: null`, R-108-12).
 *
 * ## Every figure is the API's
 *
 * The rate, the counts, the grain, the effective state and the 30-day counts
 * are printed as the API sent them. The page decides nothing: the effective
 * state is the pipeline's own decision with the sample step dropped, and the
 * pipeline's state statement is the only read that settles a field (R-108-2).
 * A rate under the minimum sample reads "too few decisions", as on CLM-107's
 * page. The state tag is neutral — no pass or fail colour — and the reason is
 * in words.
 *
 * ## Changing one field's policy
 *
 * One field at a time, never a bulk change (FR-31). The dialog offers the three
 * modes; a high-stakes field offers none (D-2.1-5: "FR-07: always reviewed by a
 * person"), and a retired field none either (the API answers 409). The request
 * carries the mode, a note and the revision this page showed — **no basis**:
 * the server captures the org-wide figure at the field's current grain itself,
 * so FR-31's audit row carries numbers no browser computed. An opt-in
 * (`auto_approve`) needs the minimum sample and a note, and takes effect only
 * once a different Legal holder co-signs it (R-108-24); the dialog says so.
 * Rows already auto-approved stay so (R-108-17); after a change the page links
 * to them in the queue, filtered on the field and `AutoApproved`.
 */

function when(instant: string | null): ReactNode {
  return instant === null ? '—' : <time dateTime={instant}>{instant}</time>;
}

function fieldCell(field: VerificationPolicyField): ReactNode {
  return (
    <>
      <code>{field.field_name}</code>
      {field.is_high_stakes ? (
        <>
          {' '}
          <span className="tag acc">High stakes</span>
        </>
      ) : null}
      {field.is_admin_field ? (
        <>
          {' '}
          <span className="tag">admin field</span>
        </>
      ) : null}
      {field.is_retired ? (
        <>
          {' '}
          <span className="tag out">retired</span>
        </>
      ) : null}
    </>
  );
}

function decisionsCell(field: VerificationPolicyField): ReactNode {
  const { basis } = field;
  return (
    <>
      <span aria-hidden="true">
        {basis.verified_count} / {basis.corrected_count} / {basis.rejected_count}
      </span>
      <span className="vh">{decisionsSpoken(basis)}</span>
    </>
  );
}

function sampleCell(field: VerificationPolicyField, minimum: number): string {
  const n = field.basis.sample_size;
  return n >= minimum ? String(n) : `${String(n)} of ${String(minimum)}`;
}

function policyCell(field: VerificationPolicyField): ReactNode {
  const { policy } = field;
  const optIn = optInCopy(policy);
  return (
    <>
      {MODE_LABELS[policy.mode]}
      {policy.policy_revision === 0 ? (
        <span className="mut"> · default</span>
      ) : (
        <span className="mut">
          {' '}
          · r{String(policy.policy_revision)} by {policy.changed_by_upn} · {when(policy.changed_utc)}
        </span>
      )}
      {optIn === null ? null : (
        <>
          <br />
          <span className="mut" data-testid="vp-opt-in-state">
            {optIn}
          </span>
        </>
      )}
    </>
  );
}

function stateCell(field: VerificationPolicyField): ReactNode {
  const copy = STATE_COPY[field.effective_state];
  return (
    <>
      <span className="tag">{copy.tag}</span>
      <br />
      <span className="mut">{copy.reason}</span>
      {field.stale_since_utc === null ? null : (
        <>
          <br />
          <span className="mut">stale since {when(field.stale_since_utc)}</span>
        </>
      )}
    </>
  );
}

function windowCell(field: VerificationPolicyField): ReactNode {
  const { auto_approved, sampled, queued } = field.last_30_days;
  return (
    <>
      <span aria-hidden="true">
        {auto_approved} / {sampled} / {queued}
      </span>
      <span className="vh">{`${String(auto_approved)} auto-approved, ${String(sampled)} sampled, ${String(queued)} queued`}</span>
      {field.auto_approved_total === 0 ? null : (
        <>
          <br />
          <Link to={autoApprovedQueueHref(field.field_name)}>
            {field.auto_approved_total} auto-approved in all — view them
          </Link>
        </>
      )}
    </>
  );
}

/** Settled by the last change, shown at the table so the re-read cannot unmount it. */
type Outcome =
  | { readonly kind: 'done'; readonly field: VerificationPolicyField; readonly text: string; readonly optIn: boolean }
  | { readonly kind: 'problem'; readonly problem: PolicyProblem };

export function FieldPolicyTable({
  fields,
  minimum,
  selfCertifyEnabled,
  onChanged,
}: {
  readonly fields: readonly VerificationPolicyField[];
  readonly minimum: number;
  /** CLM-127, D-127-1 — offers the dialog's self-certify button only when the flag is on. */
  readonly selfCertifyEnabled: boolean;
  readonly onChanged: () => void;
}) {
  const [openField, setOpenField] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const open = fields.find((field) => field.field_name === openField) ?? null;

  const columns: readonly DataTableColumn<VerificationPolicyField>[] = [
    { id: 'field', header: 'Field', cell: fieldCell },
    { id: 'grain', header: 'Grain', cell: (field) => <span className="mut">{grainCopy(field.grain)}</span> },
    { id: 'decisions', header: 'Decisions (V / C / R)', cell: decisionsCell, cellClassName: 'num' },
    { id: 'n', header: 'n', cell: (field) => sampleCell(field, minimum), cellClassName: 'num' },
    { id: 'rate', header: 'Rate', cell: (field) => rateCopy(field.basis, minimum), cellClassName: 'num' },
    { id: 'last', header: 'Last decision', cell: (field) => when(field.basis.last_decision_utc) },
    { id: 'policy', header: 'Policy', cell: policyCell },
    { id: 'state', header: 'Effective state', cell: stateCell },
    { id: 'window', header: 'Last 30 days (auto / sampled / queued)', cell: windowCell, cellClassName: 'num' },
    {
      id: 'action',
      header: 'Change',
      cell: (field) => {
        if (field.is_high_stakes) return <span className="mut">{HIGH_STAKES_COPY}</span>;
        if (field.is_retired) return <span className="mut">Retired — reactivate it to change its policy</span>;
        return (
          <button
            type="button"
            className="btn sm"
            id={`vp-change-${field.field_name}`}
            aria-label={`Change policy for ${field.field_name}`}
            aria-expanded={openField === field.field_name}
            onClick={() => {
              setOpenField(openField === field.field_name ? null : field.field_name);
              setOutcome(null);
            }}
          >
            Change policy
          </button>
        );
      },
    },
  ];

  return (
    <section className="card" aria-labelledby="vp-fields-heading" data-testid="vp-fields">
      <h3 id="vp-fields-heading">Per field</h3>
      <p className="mut">
        The organisation-wide figure at each field’s current grain: rate = verified ÷ (verified + corrected), from
        human decisions only. An admin field counts only decisions on extractions made under its current definition,
        so after an edit its n restarts and can be smaller than on Extraction accuracy.
      </p>
      <DataTable
        caption="Verification policy by field"
        columns={columns}
        rows={fields}
        rowKey={(field) => field.field_name}
        isCurrent={(field) => field.field_name === openField}
        empty={<p>No field is listed.</p>}
      />

      {open === null ? null : (
        <ChangePolicyDialog
          key={`${open.field_name}|${String(open.policy.policy_revision)}`}
          field={open}
          minimum={minimum}
          selfCertifyEnabled={selfCertifyEnabled}
          onClose={() => {
            setOpenField(null);
            document.getElementById(`vp-change-${open.field_name}`)?.focus();
          }}
          onOutcome={(next) => {
            setOutcome(next);
            if (next.kind === 'done') setOpenField(null);
          }}
          onChanged={onChanged}
        />
      )}

      <div aria-live="polite" data-testid="vp-field-outcome">
        {outcome?.kind === 'done' ? (
          <div className="banner up" role="status">
            <p>{outcome.text}</p>
            {outcome.optIn ? <p>{OPT_IN_TAKES_EFFECT_COPY}</p> : null}
            {outcome.field.auto_approved_total === 0 ? null : (
              <p>
                {outcome.field.auto_approved_total} value{outcome.field.auto_approved_total === 1 ? '' : 's'}{' '}
                auto-approved under earlier policy stay so —{' '}
                <Link to={autoApprovedQueueHref(outcome.field.field_name)}>view them</Link>
              </p>
            )}
          </div>
        ) : null}
        {outcome?.kind === 'problem' ? <PolicyProblemNotice problem={outcome.problem} /> : null}
      </div>
    </section>
  );
}

function ChangePolicyDialog({
  field,
  minimum,
  selfCertifyEnabled,
  onClose,
  onOutcome,
  onChanged,
}: {
  readonly field: VerificationPolicyField;
  readonly minimum: number;
  /** CLM-127, D-127-1 — the self-certify button is offered only when the flag is on, and only for an opt-in. */
  readonly selfCertifyEnabled: boolean;
  readonly onClose: () => void;
  readonly onOutcome: (outcome: Outcome) => void;
  readonly onChanged: () => void;
}) {
  const [mode, setMode] = useState<VerificationPolicyMode>(field.policy.mode);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const { basis, policy } = field;
  const tooFew = basis.sample_size < minimum;
  /* An opt-in that lapsed or that Legal declined may be taken again — the API's own rule
   * (`retakesOptIn` in routes/verification-policy.ts): a new revision, for a fresh co-sign.
   * Anything else must move the mode. */
  const retake = mode === 'auto_approve' && (policy.opt_in_lapsed || policy.cosign?.decision === 'decline');
  const unchanged = mode === policy.mode && !retake;
  /* A lapsed opt-in at a new grain usually has n < 30 there (a new model starts at 0), so the
   * dialog can open on a mode whose radio is disabled. Saving it could only be refused
   * (too_few_decisions, D-2.1-2), so Save waits for a choice that is enabled. */
  const choiceDisabled = mode === 'auto_approve' && tooFew;

  async function save() {
    const trimmed = note.trim();
    if (mode === 'auto_approve' && trimmed === '') {
      setNoteError(OPT_IN_NOTE_COPY);
      return;
    }
    setNoteError(null);
    setBusy(true);
    try {
      const response = await setFieldVerificationPolicy({
        field_name: field.field_name,
        // The revision this page showed: another admin's change meanwhile is 409 policy_changed, never an overwrite.
        expected_policy_revision: policy.policy_revision,
        mode,
        ...(trimmed === '' ? {} : { change_note: trimmed }),
      });
      onOutcome({
        kind: 'done',
        field,
        optIn: mode === 'auto_approve',
        text:
          `Recorded revision r${String(response.policy.policy_revision)} for ${field.field_name}: ` +
          `${MODE_LABELS[response.policy.mode]}. Effective now: ${STATE_COPY[response.effective_state].tag} ` +
          `(audit row ${response.audit.audit_id}).`,
      });
      onChanged();
    } catch (error) {
      const problem = policyProblemOf(error);
      onOutcome({ kind: 'problem', problem });
      if (problem.refusal?.reload === true) onChanged();
    } finally {
      setBusy(false);
    }
  }

  /**
   * CLM-127, D-127-1 — the same draft, posted to the self-certify endpoint:
   * one request both sets the opt-in and co-signs it, under this Admin's own
   * identity. Offered only for `auto_approve` (R-127-14 fixes the mode
   * server-side; the wire request carries no `mode` at all).
   */
  async function saveSelfCertified() {
    const trimmed = note.trim();
    if (trimmed === '') {
      setNoteError(OPT_IN_NOTE_COPY);
      return;
    }
    setNoteError(null);
    setBusy(true);
    try {
      const response = await selfCertifyFieldVerificationPolicy({
        field_name: field.field_name,
        expected_policy_revision: policy.policy_revision,
        change_note: trimmed,
      });
      onOutcome({
        kind: 'done',
        field,
        optIn: true,
        text:
          `Self-certified revision r${String(response.policy.policy_revision)} for ${field.field_name}: ` +
          `${MODE_LABELS[response.policy.mode]}. Effective now: ${STATE_COPY[response.effective_state].tag} ` +
          `(audit row ${response.audit.audit_id}). Not independently reviewed by Legal.`,
      });
      onChanged();
    } catch (error) {
      const problem = policyProblemOf(error);
      onOutcome({ kind: 'problem', problem });
      if (problem.refusal?.reload === true) onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vpanel" role="group" aria-labelledby="vp-dialog-heading" data-testid="vp-change-dialog">
      <h4 id="vp-dialog-heading">Change the policy for {field.field_name}</h4>
      <p className="mut">
        In force: {MODE_LABELS[policy.mode]}
        {policy.policy_revision === 0 ? ' (the default)' : ` — revision r${String(policy.policy_revision)}`}.
      </p>
      <fieldset>
        <legend>Policy</legend>
        {VERIFICATION_POLICY_MODES.map((candidate) => {
          const disabled = busy || (candidate === 'auto_approve' && tooFew);
          return (
            <label key={candidate} className="check">
              <input
                type="radio"
                name={`vp-mode-${field.field_name}`}
                value={candidate}
                checked={mode === candidate}
                disabled={disabled}
                onChange={() => {
                  setMode(candidate);
                  setNoteError(null);
                }}
              />{' '}
              <strong>{MODE_LABELS[candidate]}</strong> — {MODE_HELP[candidate]}
              {candidate === 'auto_approve' && tooFew ? (
                <span className="mut">
                  {' '}
                  Needs {minimum} human decisions at the current grain; this field has {basis.sample_size} (D-2.1-2).
                </span>
              ) : null}
            </label>
          );
        })}
      </fieldset>

      <p className="mut" data-testid="vp-dialog-basis">
        Recorded against the basis the server reads at the field’s current grain ({grainCopy(field.grain)}) — on this
        page: {basis.verified_count} / {basis.corrected_count} / {basis.rejected_count}
        <span className="vh"> ({decisionsSpoken(basis)})</span>, n = {basis.sample_size}, rate {rateCopy(basis, minimum)}.
      </p>

      {mode === 'auto_approve' ? (
        <p className="banner up" role="note" data-testid="vp-opt-in-cosign-note">
          {OPT_IN_TAKES_EFFECT_COPY}
        </p>
      ) : null}

      <div className="field">
        <label htmlFor="vp-change-note">{mode === 'auto_approve' ? 'Why (required for an opt-in)' : 'Note (optional)'}</label>
        <textarea
          id="vp-change-note"
          rows={2}
          maxLength={500}
          value={note}
          disabled={busy}
          onChange={(event) => setNote(event.target.value)}
        />
        {mode === 'auto_approve' ? <p className="mut">{OPT_IN_NOTE_COPY}</p> : null}
      </div>

      <div className="vacts">
        <button type="button" className="btn pri" disabled={busy || unchanged || choiceDisabled} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save policy'}
        </button>
        {selfCertifyEnabled && mode === 'auto_approve' ? (
          <button
            type="button"
            className="btn qt"
            disabled={busy || unchanged || choiceDisabled}
            data-testid="vp-self-certify-field"
            onClick={() => void saveSelfCertified()}
            title="Set and decide this opt-in yourself in one step — not independently reviewed by Legal (CLM-127, D-127-1)."
          >
            {busy ? 'Self-certifying…' : 'Save and self-certify'}
          </button>
        ) : null}
        <button type="button" className="btn qt" disabled={busy} onClick={onClose}>
          Cancel
        </button>
      </div>
      {noteError === null ? null : (
        <p className="ferr" role="alert">
          {noteError}
        </p>
      )}
    </div>
  );
}
